import { cssClass, Style } from '@tableau/data-catalog-shared';
import { TableauExpressionLexer } from '@tableau/tableau-expression-parser-js';
import { Token } from 'antlr4';
import * as React from 'react';

import { tokensFromTableauExpression, trimBrackets } from '../functions/TableauExpressionFunctions';

export type FieldInfo = {
  readonly id: string;
  readonly name: string | null;
};

export type ParameterInfo = {
  readonly name: string | null;
};

export type TableauExpressionFormatterProps = {
  readonly expression: string;
  readonly fields: ReadonlyArray<FieldInfo> | null;
  readonly parameters: ReadonlyArray<ParameterInfo> | null;
  readonly makeFieldUrl: (fieldId: string) => string;
  readonly openLinksInNewTab?: boolean;
};

export class TableauExpressionFormatter extends React.Component<TableauExpressionFormatterProps> {

  public render(): JSX.Element {
    const tokens = tokensFromTableauExpression(this.props.expression);
    return (
      <span className={expressionClass}>{tokens.map(this.renderToken)}</span>
    );
  }

  private readonly renderToken = (token: Token, index: number): JSX.Element | null => {
    if (token.text) {
      const text = token.text;
      switch (token.type) {
        case TableauExpressionLexer.COMMENT:
          return this.renderTokenText(text, index, stringClass);
        case TableauExpressionLexer.FIELDNAME:
          return this.renderFieldName(text, index);
        case TableauExpressionLexer.IDENTIFIER:
          return this.renderIdentifier(text, index);
        case TableauExpressionLexer.STRINGLITERAL:
          return this.renderTokenText(text, index, stringClass);
        case TableauExpressionLexer.LCBRACK:
        case TableauExpressionLexer.RCBRACK:
        case TableauExpressionLexer.COLON:
        case TableauExpressionLexer.FIXED:
        case TableauExpressionLexer.INCLUDE:
        case TableauExpressionLexer.EXCLUDE:
          return this.renderTokenText(text, index, functionClass);
        default:
          return this.renderTokenText(text, index);
      }
    } else {
      return null;
    }
  }

  private renderFieldName(text: string, index: number): JSX.Element {
    const name = trimBrackets(text);
    return this.renderFieldOrParameter(name, text, index);
  }

  private renderIdentifier(text: string, index: number): JSX.Element {
    const name = text;
    return this.renderFieldOrParameter(name, text, index);
  }

  private renderFieldOrParameter(name: string, text: string, index: number): JSX.Element {
    const fieldInfo = getFieldByName(name, this.props.fields);
    if (fieldInfo) {
      return (
        <a
          key={index}
          className={fieldNameClass}
          href={this.props.makeFieldUrl(fieldInfo.id)}
          target={this.props.openLinksInNewTab ? '_blank' : undefined} rel="noreferrer"
        >
          {text}
        </a>
      );
    } else {
      const parameterInfo = getParameterByName(name, this.props.parameters);
      if (parameterInfo) {
        return this.renderTokenText(text, index, parameterClass);
      } else {
        return this.renderTokenText(text, index);
      }
    }
  }

  private renderTokenText(text: string, index: number, className?: string): JSX.Element {
    return (
      <span key={index} className={className}>{text}</span>
    );
  }

}

const fieldNameStyle: Style = {
  color: '#f18a34',
  textDecoration: 'none',
  $nest: {
    '&:hover': {
      textDecoration: 'underline',
    }
  }
};

const functionStyle: Style = {
  color: '#377e9c',
};

const parameterStyle: Style = {
  color: '#936eb0',
};

const stringStyle: Style = {
  color: '#717171',
};


const fieldNameClass = cssClass('TableauExpression_FieldName', fieldNameStyle);
const functionClass = cssClass('TableauExpression_Function', functionStyle);
const parameterClass = cssClass('TableauExpression_Parameter', parameterStyle);
const stringClass = cssClass('TableauExpression_String', stringStyle);

const expressionStyle: Style = {
  fontFamily: 'Courier New, monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  $nest: {
    [`a.${fieldNameClass}`]: {
      color: '#f18a34',
    }
  }
};

const expressionClass = cssClass('TableauExpression_Expression', expressionStyle);

function getFieldByName(name: string, fields: ReadonlyArray<FieldInfo> | null): FieldInfo | null {
  if (fields) {
    for (const fieldInfo of fields) {
      if (fieldInfo.name && fieldInfo.name === name) {
        return fieldInfo;
      }
    }
  }
  return null;
}

function getParameterByName(name: string, parameters: ReadonlyArray<ParameterInfo> | null): ParameterInfo | null {
  if (parameters) {
    for (const parameterInfo of parameters) {
      if (parameterInfo.name && parameterInfo.name === name) {
        return parameterInfo;
      }
    }
  }
  return null;
}
